import { useEffect, useState } from "react";
import {
  Page,
  Card,
  BlockStack,
  Text,
  Badge,
  Button,
  InlineStack,
  InlineGrid,
  Banner,
  Select,
  TextField,
  FormLayout,
  RadioButton,
  Checkbox,
  DataTable,
  Modal,
} from "@shopify/polaris";
import { api, ApiRequestError } from "../api";
import posthog from "../posthog";
import type { BillingPlan, CredentialSummary, FallbackBox, PackageType, Printer, Shop, ShopMode } from "../types";
import DeliverySourcesCard from "../components/DeliverySourcesCard";
import RatePreviewCard from "../components/RatePreviewCard";
import PromiseConflictsCard from "../components/PromiseConflictsCard";
import WeightCoverageCard from "../components/WeightCoverageCard";

const PLAN_TONE: Record<Shop["plan_status"], "success" | "info" | "critical" | "warning"> = {
  trial: "info",
  active: "success",
  cancelled: "critical",
  frozen: "warning",
};

const PACKAGE_TYPE_OPTIONS: Array<{ label: string; value: PackageType }> = [
  { label: "Box", value: "BOX" },
  { label: "Envelope", value: "ENVELOPE" },
  { label: "Soft pack", value: "SOFT_PACK" },
  { label: "Flat rate", value: "FLAT_RATE" },
];

export default function Settings(): JSX.Element {
  const [shop, setShop] = useState<Shop | null>(null);
  // The plan the merchant would be charged for, straight from the server. Not a
  // constant in this file: it was one, and it said $29 for a $49 plan.
  const [plan, setPlan] = useState<BillingPlan | null>(null);
  const [credentials, setCredentials] = useState<CredentialSummary[]>([]);
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [defaultPrinterId, setDefaultPrinterId] = useState("");
  const [box, setBox] = useState<Record<keyof Omit<FallbackBox, "package_type">, string>>({
    length_in: "",
    width_in: "",
    height_in: "",
  });
  const [packageType, setPackageType] = useState<PackageType>("BOX");

  const [easypostKey, setEasypostKey] = useState("");
  const [savingEasypost, setSavingEasypost] = useState(false);
  const [testingEasypost, setTestingEasypost] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [subscribing, setSubscribing] = useState(false);
  const [savingBox, setSavingBox] = useState(false);
  const [savingPrinter, setSavingPrinter] = useState(false);
  const [capabilities, setCapabilities] = useState<string | null>(null);
  const [loadingCapabilities, setLoadingCapabilities] = useState(false);
  const [savingMode, setSavingMode] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [overscan, setOverscan] = useState("0");
  const [savingOverscan, setSavingOverscan] = useState(false);

  useEffect(() => {
    // The plan is fetched alongside, but failing to load it must not take the
    // whole Settings page with it — every other card here still works without it.
    api
      .get<{ plans: BillingPlan[] }>("/billing/plans")
      .then(({ plans }) => setPlan(plans[0] ?? null))
      .catch(() => setPlan(null));

    Promise.all([api.get<Shop>("/shop"), api.get<{ credentials: CredentialSummary[] }>("/credentials")]).then(
      ([shopData, credentialsData]) => {
        setShop(shopData);
        setCredentials(credentialsData.credentials);
        setBox({
          length_in: String(shopData.fallback_box.length_in),
          width_in: String(shopData.fallback_box.width_in),
          height_in: String(shopData.fallback_box.height_in),
        });
        setPackageType(shopData.fallback_box.package_type);
        setOverscan(String(shopData.label_overscan_percent));

        const printnode = credentialsData.credentials.find((c) => c.kind === "printnode");
        if (!printnode) return;
        setDefaultPrinterId(printnode.metadata?.default_printer_id ?? "");
        api
          .get<{ printers: Printer[] }>("/printers")
          .then(({ printers: fetched }) => setPrinters(fetched))
          .catch(() => setPrinters([]));
      }
    );
  }, []);

  // Optimistic: the radio flips immediately and rolls back on failure, because
  // a toggle that lags behind the click reads as broken.
  async function saveMode(mode: ShopMode): Promise<void> {
    const previous = shop?.mode;
    setShop((current) => (current ? { ...current, mode } : current));
    setSavingMode(true);
    setError(null);
    try {
      await api.put("/shop/mode", { mode });
      posthog.capture("shipping_mode_changed", { mode });
      setToast(mode === "auto" ? "Auto mode on" : "Manual mode on");
    } catch (err) {
      setShop((current) => (current && previous ? { ...current, mode: previous } : current));
      setError(err instanceof ApiRequestError ? err.body.message : "Could not change mode");
    } finally {
      setSavingMode(false);
    }
  }

  async function savePackingList(enabled: boolean): Promise<void> {
    const previous = shop?.print_packing_list;
    setShop((current) => (current ? { ...current, print_packing_list: enabled } : current));
    setError(null);
    try {
      await api.put("/shop/packing-list", { enabled });
    } catch (err) {
      setShop((current) =>
        current && previous !== undefined ? { ...current, print_packing_list: previous } : current
      );
      setError(err instanceof ApiRequestError ? err.body.message : "Could not change packing list setting");
    }
  }

  async function savePrintReverse(enabled: boolean): Promise<void> {
    const previous = shop?.print_reverse;
    setShop((current) => (current ? { ...current, print_reverse: enabled } : current));
    setError(null);
    try {
      await api.put("/shop/print-reverse", { enabled });
    } catch (err) {
      setShop((current) => (current && previous !== undefined ? { ...current, print_reverse: previous } : current));
      setError(err instanceof ApiRequestError ? err.body.message : "Could not change print order setting");
    }
  }

  async function saveOverscan(): Promise<void> {
    const percent = parseInt(overscan, 10) || 0;
    setSavingOverscan(true);
    setError(null);
    try {
      await api.put("/shop/label-overscan", { percent });
      setShop((current) => (current ? { ...current, label_overscan_percent: percent } : current));
      setToast(percent === 0 ? "Label printed at its true size" : `Label enlarged by ${percent}%`);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.body.message : "Could not save label overscan");
    } finally {
      setSavingOverscan(false);
    }
  }

  async function testEasyPost(): Promise<void> {
    setTestingEasypost(true);
    setError(null);
    try {
      const result = await api.post<{ valid: boolean; error?: string; carrier_accounts?: string[] | null }>(
        "/verify-easypost",
        { apiKey: easypostKey }
      );
      if (!result.valid) {
        setError(result.error ?? "EasyPost rejected this API key");
      } else if (result.carrier_accounts && result.carrier_accounts.length === 0) {
        // Authenticating and being able to buy are different things. An account
        // with no carriers rates empty on every order and says nothing about
        // why, so "accepted" on its own would be a misleading success.
        setError(
          "EasyPost accepted this key, but the account has no carrier accounts enabled — no carrier will quote a rate. Add one in EasyPost, then run the test rate shop below."
        );
      } else if (result.carrier_accounts && result.carrier_accounts.length > 0) {
        setToast(`EasyPost accepted this key · ${result.carrier_accounts.join(", ")}`);
      } else {
        setToast("EasyPost accepted this key");
      }
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.body.message : "Could not reach EasyPost");
    } finally {
      setTestingEasypost(false);
    }
  }

  async function saveEasyPost(): Promise<void> {
    setSavingEasypost(true);
    setError(null);
    try {
      const saved = await api.patch<{ ok: true; carrier_accounts?: string[] | null }>("/settings/easypost", {
        apiKey: easypostKey,
      });
      setEasypostKey("");
      const { credentials: refreshed } = await api.get<{ credentials: CredentialSummary[] }>("/credentials");
      setCredentials(refreshed);
      // Same three-way read as the Test button, because saving without testing
      // first is the common path and is exactly where a key with no carriers
      // behind it would otherwise pass silently. null means EasyPost would not
      // say; an empty array is a diagnosis.
      if (saved.carrier_accounts && saved.carrier_accounts.length === 0) {
        setError(
          "EasyPost key saved, but the account has no carrier accounts enabled — no carrier will quote a rate. Add one in EasyPost, then run the test rate shop below."
        );
      } else if (saved.carrier_accounts && saved.carrier_accounts.length > 0) {
        setToast(`EasyPost key saved · ${saved.carrier_accounts.join(", ")}`);
      } else {
        setToast("EasyPost key saved");
      }
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.body.message : "Could not save EasyPost key");
    } finally {
      setSavingEasypost(false);
    }
  }

  async function disconnect(): Promise<void> {
    await api.delete("/credentials/printnode");
    setCredentials((current) => current.filter((c) => c.kind !== "printnode"));
    setPrinters([]);
    setDefaultPrinterId("");
  }

  async function saveDefaultPrinter(printerId: string): Promise<void> {
    setDefaultPrinterId(printerId);
    setSavingPrinter(true);
    setError(null);
    try {
      await api.post("/printers/default", { printer_id: printerId });
      setToast("Default printer updated");
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.body.message : "Could not set default printer");
    } finally {
      setSavingPrinter(false);
    }
  }

  async function testPrint(): Promise<void> {
    setError(null);
    try {
      await api.post(`/printers/${defaultPrinterId}/test-print`);
      setToast("Test print sent");
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.body.message : "Could not send test print");
    }
  }

  // Sizing complaints ("the label prints half size") come down to what the
  // driver says its media is, which is only visible from inside the merchant's
  // network. This puts that JSON somewhere they can paste it into a support
  // thread.
  async function loadCapabilities(): Promise<void> {
    setLoadingCapabilities(true);
    setError(null);
    try {
      const response = await api.get<unknown>("/debug/printer-capabilities");
      setCapabilities(JSON.stringify(response, null, 2));
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.body.message : "Could not read printer capabilities");
    } finally {
      setLoadingCapabilities(false);
    }
  }

  async function saveFallbackBox(): Promise<void> {
    setSavingBox(true);
    setError(null);
    try {
      const payload: FallbackBox = {
        length_in: parseFloat(box.length_in),
        width_in: parseFloat(box.width_in),
        height_in: parseFloat(box.height_in),
        package_type: packageType,
      };
      await api.put("/shop/fallback-box", payload);
      setShop((current) => (current ? { ...current, fallback_box: payload, fallback_box_configured: true } : current));
      setToast("Fallback box saved");
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.body.message : "Could not save fallback box");
    } finally {
      setSavingBox(false);
    }
  }

  async function cleanUpFailedJobs(): Promise<void> {
    setCleaningUp(true);
    setError(null);
    try {
      const { deleted } = await api.post<{ deleted: number }>("/print-jobs/cleanup-failed");
      setToast(
        deleted === 0
          ? "No obsolete failure rows to remove"
          : `Removed ${deleted} obsolete failure row${deleted === 1 ? "" : "s"}`
      );
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.body.message : "Could not clean up failed jobs");
    } finally {
      setCleaningUp(false);
    }
  }

  // Starts a NEW subscription, which is all this can do — approving one replaces
  // whatever the shop already had. So it is only reachable below when the shop is
  // not already on an active one; it used to be labelled "Manage subscription"
  // and shown to everyone, which meant a paying merchant pressing it restarted
  // their billing period.
  async function startSubscription(): Promise<void> {
    setSubscribing(true);
    setError(null);
    try {
      const { confirmation_url } = await api.post<{ confirmation_url: string }>("/billing/subscribe");
      posthog.capture("subscription_started");
      window.top?.location.assign(confirmation_url);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.body.message : "Could not start subscription");
    } finally {
      setSubscribing(false);
    }
  }

  const printnode = credentials.find((c) => c.kind === "printnode");
  const easypost = credentials.find((c) => c.kind === "easypost");
  const boxComplete = Object.values(box).every((v) => parseFloat(v) > 0);

  return (
    <Page title="Settings">
      <BlockStack gap="400">
        {error && <Banner tone="critical">{error}</Banner>}
        {toast && <Banner tone="success">{toast}</Banner>}

        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">
              How ShipPromise handles paid orders
            </Text>
            <BlockStack gap="200">
              <RadioButton
                label="⚡ Auto mode"
                helpText="Labels are bought and printed automatically when orders are paid."
                checked={shop?.mode === "auto"}
                disabled={!shop || savingMode}
                id="mode-auto"
                name="shop-mode"
                onChange={() => void saveMode("auto")}
              />
              <RadioButton
                label="📋 Manual mode"
                helpText="Orders are queued, and you buy and print them from the Orders tab."
                checked={shop?.mode === "manual"}
                disabled={!shop || savingMode}
                id="mode-manual"
                name="shop-mode"
                onChange={() => void saveMode("manual")}
              />
            </BlockStack>
            <Checkbox
              label="Print packing list with label"
              helpText="A 4×6 slip with the order contents, printed as the first page of the same job so the picker sees it on top."
              checked={shop?.print_packing_list ?? true}
              disabled={!shop}
              onChange={(value) => void savePackingList(value)}
            />
            <Checkbox
              label="Reverse print order"
              helpText="Turn this on if the label comes off the roll ahead of the packing list. Some drivers print the last page of a job first."
              checked={shop?.print_reverse ?? false}
              disabled={!shop || !(shop?.print_packing_list ?? true)}
              onChange={(value) => void savePrintReverse(value)}
            />
          </BlockStack>
        </Card>

        <DeliverySourcesCard />

        <PromiseConflictsCard />

        <WeightCoverageCard />

        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">
              EasyPost
            </Text>
            <Text as="p" tone="subdued">
              ShipPromise buys every label through EasyPost, comparing live rates across your enabled
              carrier accounts and buying the cheapest one that matches the rule.
            </Text>

            <Text as="span" fontWeight="semibold">
              {easypost ? `Connected · key ending ${easypost.masked.slice(-4)}` : "Not connected"}
            </Text>

            <FormLayout>
              <TextField
                label={easypost ? "Replace API key" : "EasyPost API key"}
                value={easypostKey}
                onChange={setEasypostKey}
                autoComplete="off"
                type="password"
                helpText="Stored encrypted. Only the last four characters are ever shown again."
              />
              <InlineStack gap="200">
                <Button onClick={testEasyPost} loading={testingEasypost} disabled={!easypostKey}>
                  Test
                </Button>
                <Button variant="primary" onClick={saveEasyPost} loading={savingEasypost} disabled={!easypostKey}>
                  Save
                </Button>
              </InlineStack>
            </FormLayout>
          </BlockStack>
        </Card>

        {/* Directly under the EasyPost card: this is the answer to "did that
            work?", and it is the only way to find out short of a real paid
            order. Rendered whether or not a key is connected — the route says
            so plainly, which is more useful than a card that vanishes. */}
        <RatePreviewCard />

        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">
              Plan
            </Text>
            {shop && (
              <InlineStack gap="200" blockAlign="center">
                <Badge tone={shop.entitled ? PLAN_TONE[shop.plan_status] : "critical"}>
                  {shop.trial_expired ? "trial ended" : shop.plan_status}
                </Badge>
                {plan && (
                  <Text as="span" tone="subdued">
                    {plan.priceUsd ? `$${plan.priceUsd}/month` : "Free"}
                    {shop.remaining_trial_days > 0 &&
                      ` · ${shop.remaining_trial_days}-day free trial remaining`}
                  </Text>
                )}
              </InlineStack>
            )}
            {/* An unentitled shop stops buying labels, so say so here rather than
                letting the merchant discover it as a failed order. */}
            {shop && !shop.entitled && (
              <Banner tone="critical" title="ShipPromise is not buying labels">
                <Text as="p">
                  {shop.trial_expired
                    ? "The 14-day free trial has ended. Subscribe to start buying and printing labels again."
                    : `This shop's plan is ${shop.plan_status}. Subscribe to start buying and printing labels again.`}
                </Text>
              </Banner>
            )}
            {shop?.entitled && shop.plan_status === "trial" && shop.trial_ends_at && (
              <Text as="p" tone="subdued">
                Free trial ends {new Date(shop.trial_ends_at.replace(" ", "T") + "Z").toLocaleDateString()}.
              </Text>
            )}
            {/* An active subscription is managed in Shopify, not here. This app
                can only ever create a subscription, and approving a new one
                replaces the existing one — so offering the button to a paying
                merchant restarted their billing period instead of managing it. */}
            {shop?.plan_status === "active" ? (
              <Text as="p" tone="subdued">
                Billed through Shopify. Change or cancel this subscription in Shopify under Settings → Apps
                and sales channels.
              </Text>
            ) : (
              <InlineStack>
                <Button
                  onClick={startSubscription}
                  loading={subscribing}
                  variant={shop && !shop.entitled ? "primary" : undefined}
                >
                  Subscribe
                </Button>
              </InlineStack>
            )}
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">
              PrintNode
            </Text>

            <InlineStack align="space-between" blockAlign="center">
              <BlockStack gap="050">
                <Text as="span" fontWeight="semibold">
                  {printnode ? "Connected" : "Not connected"}
                </Text>
                {printnode && (
                  <Text as="span" tone="subdued">
                    Key ending {printnode.masked.slice(-4)}
                  </Text>
                )}
              </BlockStack>
              {printnode && (
                <Button tone="critical" onClick={disconnect}>
                  Disconnect
                </Button>
              )}
            </InlineStack>

            {printnode && printers.length === 0 && (
              <Banner tone="warning">
                No printers found on this PrintNode account. Install the PrintNode client on your
                shipping computer, then reload this page.
              </Banner>
            )}

            {printnode && printers.length > 0 && (
              <FormLayout>
                <Select
                  label="Default printer"
                  options={printers.map((p) => ({ label: `${p.name} (${p.state})`, value: String(p.id) }))}
                  value={defaultPrinterId}
                  onChange={saveDefaultPrinter}
                  disabled={savingPrinter}
                  helpText="Every label ShipPromise buys is printed here."
                />
                <InlineStack gap="200">
                  <Button onClick={testPrint} disabled={!defaultPrinterId}>
                    Send test print
                  </Button>
                  <Button onClick={loadCapabilities} disabled={!defaultPrinterId} loading={loadingCapabilities}>
                    Debug printer capabilities
                  </Button>
                </InlineStack>
                <TextField
                  label="Label overscan (%)"
                  type="number"
                  min={0}
                  max={5}
                  value={overscan}
                  onChange={setOverscan}
                  autoComplete="off"
                  connectedRight={
                    <Button onClick={saveOverscan} loading={savingOverscan}>
                      Save
                    </Button>
                  }
                  helpText="Leave at 0. Only raise this if a thin white border survives on every label. It enlarges the label past the edge of the stock, and whatever was at the edge is cut off."
                />
              </FormLayout>
            )}
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">
              Activity log
            </Text>
            <Text as="p" tone="subdued">
              Before ShipPromise deduplicated webhook deliveries, a redelivered order could leave extra
              &quot;Failed&quot; rows next to a label that actually printed. This removes those rows.
              A failure is only removed when the same shipment is recorded as printed, so a genuine
              failure on a split shipment is left alone.
            </Text>
            <InlineStack>
              <Button onClick={cleanUpFailedJobs} loading={cleaningUp}>
                Clean up old failed jobs
              </Button>
            </InlineStack>
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">
              Fallback box
            </Text>
            <Text as="p" tone="subdued">
              Used only when a product in the order has no dimensions saved in Shopify. Weight is
              never taken from here. It comes from the variant&apos;s own shipping weight in
              Shopify, falling back to 16 oz for products that have none.
            </Text>
            {shop && !shop.fallback_box_configured && (
              <Banner tone="warning">
                No fallback box saved yet. ShipPromise is using 10 × 8 × 4 in until you set one.
              </Banner>
            )}
            <Banner tone="info" title="Per-product dimensions">
              <BlockStack gap="200">
                <Text as="p">
                  Shopify has no dimensions field of its own, but it will hold one for you. Add these
                  three variant metafields in{" "}
                  <strong>Settings → Custom data → Variants</strong>, each of type{" "}
                  <strong>Dimension</strong>, and ShipPromise uses them instead of the box below.
                  Every item in a shipment needs all three, or the fallback box is used for the whole
                  parcel.
                </Text>
                <DataTable
                  columnContentTypes={["text", "text"]}
                  headings={["Namespace and key", "Type"]}
                  rows={[
                    ["shippromise.box_length", "Dimension"],
                    ["shippromise.box_width", "Dimension"],
                    ["shippromise.box_height", "Dimension"],
                  ]}
                />
                {/* Documented App Bridge protocol for admin links. The Custom data
                    settings page is the closest target Shopify exposes. There is no
                    deep link that pre-fills a definition, so the path above stands on
                    its own if this does not resolve. */}
                <a href="shopify://admin/settings/custom_data" target="_top">
                  Open Custom data settings
                </a>
              </BlockStack>
            </Banner>
            <FormLayout>
              <InlineGrid columns={{ xs: 1, sm: 3 }} gap="300">
                <TextField
                  label="Length (in)"
                  type="number"
                  value={box.length_in}
                  onChange={(v) => setBox({ ...box, length_in: v })}
                  autoComplete="off"
                />
                <TextField
                  label="Width (in)"
                  type="number"
                  value={box.width_in}
                  onChange={(v) => setBox({ ...box, width_in: v })}
                  autoComplete="off"
                />
                <TextField
                  label="Height (in)"
                  type="number"
                  value={box.height_in}
                  onChange={(v) => setBox({ ...box, height_in: v })}
                  autoComplete="off"
                />
              </InlineGrid>
              <Select
                label="Package type"
                options={PACKAGE_TYPE_OPTIONS}
                value={packageType}
                onChange={(v) => setPackageType(v as PackageType)}
              />
              <InlineStack align="end">
                <Button variant="primary" onClick={saveFallbackBox} loading={savingBox} disabled={!boxComplete}>
                  Save fallback box
                </Button>
              </InlineStack>
            </FormLayout>
          </BlockStack>
        </Card>
      </BlockStack>

      <Modal
        open={capabilities !== null}
        onClose={() => setCapabilities(null)}
        title="Printer capabilities"
        secondaryActions={[{ content: "Close", onAction: () => setCapabilities(null) }]}
      >
        <Modal.Section>
          <BlockStack gap="200">
            <Text as="p" tone="subdued">
              What the printer driver reports to PrintNode, plus the options ShipPromise sends with a
              label. Paper sizes are in tenths of a millimetre. A 4x6in label is 1016 x 1524.
            </Text>
            <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-all", fontSize: 12, margin: 0 }}>
              {capabilities}
            </pre>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
